// ─────────────────────────────────────────────────────────────────────────────
// Parsed label → a standalone SVG string, and the print document around it.
//
// The counter PC prints pouch labels by handing raw PRN bytes to the print
// bridge, which is the only way to get a SATO to lay a tag out exactly as the
// template says. A phone has no bridge and no printer port: its Bluetooth label
// printer is driven by an Android print service, which only ever receives a
// *rendered page* from Chrome. So the same design has to be drawn rather than
// commanded.
//
// This module is that renderer. It takes the layout parseLabel() already
// extracts from the PRN and the token values the backend already resolves, and
// paints them into one SVG at the label's real millimetre size — the same two
// inputs the mapper's on-screen previewer uses, so the phone and the SATO can
// never drift apart.
//
// Why one SVG and not HTML: HTML reflows. A 50 × 25 mm page is ~189 CSS px
// wide, and any block of text laid out at that width wraps, overflows, and
// paginates — which is exactly how a one-label print job turns into sixteen
// pages. SVG has fixed internal geometry; a viewBox scaled to the paper can
// only ever be one page.
// ─────────────────────────────────────────────────────────────────────────────

import React from "react";
import { code128Bars, code39Bars } from "../paymentScreen/barcode";
import { DPMM } from "./prnParse";

// QR is the one thing here that cannot be drawn from first principles in a few
// lines, and most label designs do not carry one. Its encoder — and the
// server renderer needed to turn a React element into markup — are therefore
// loaded on first use rather than shipped to every session that never prints a
// QR label. Cached after the first call; a label may hold several.
let qrMarkup = null;
const ensureQrRenderer = async () => {
  if (!qrMarkup) {
    const [server, qr] = await Promise.all([
      import("react-dom/server"),
      import("qrcode.react"),
    ]);
    qrMarkup = (props) =>
      server.renderToStaticMarkup(React.createElement(qr.QRCodeSVG, props));
  }
  return qrMarkup;
};

// Media size used when a template does not declare one (and by the built-in
// sticker) — the common pouch/receipt label stock.
export const DEFAULT_LABEL_MM = { w: 50, h: 25 };

const XML_ESC = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&apos;",
};
const esc = (s) =>
  String(s == null ? "" : s).replace(/[&<>"']/g, (c) => XML_ESC[c]);

const TOKEN_RE = /<([A-Z][A-Z0-9_]*)>/g;

// Substitute a field's tokens from the backend's resolved map. An unmapped
// token prints as nothing, exactly as the real print job does — a label that
// invented a value would be worse than one with a gap.
const fillTokens = (raw, resolved) => {
  let missing = false;
  const text = String(raw == null ? "" : raw).replace(TOKEN_RE, (_m, tok) => {
    const v = resolved?.[tok];
    if (v == null || v === "") {
      missing = true;
      return "";
    }
    return v;
  });
  return { text, missing };
};

// Advance width per character — see LabelPreview for why the scalable faces'
// declared width is treated as a cap rather than a stretch factor.
const charWidth = (el) => (el.mono && el.w > 0 ? el.w : el.h * 0.52);

// QR module count by payload size (byte mode, level M); only sizes the box.
const qrModules = (data) => {
  const l = String(data || "").length;
  if (l <= 14) return 21;
  if (l <= 26) return 25;
  if (l <= 42) return 29;
  if (l <= 62) return 33;
  if (l <= 84) return 37;
  if (l <= 106) return 41;
  return 45;
};

const attr = (o) =>
  Object.entries(o)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k}="${esc(v)}"`)
    .join(" ");

const rect = (x, y, w, h, fill) =>
  `<rect ${attr({
    x,
    y,
    width: Math.max(0, w),
    height: Math.max(0, h),
    fill,
  })}/>`;

// ─── PRN layout → SVG ───────────────────────────────────────────────────────

/**
 * Draw a parsed PRN layout, with its tokens filled in, as a self-contained SVG.
 *
 * Coordinates stay in the printer's dot space (203 dpi) and the SVG carries the
 * media size in millimetres, so the page it lands on decides the scale and the
 * design keeps its proportions whatever paper the phone's print service offers.
 *
 * @param {object}  parsed    parseLabel() output
 * @param {object}  resolved  token → final string, from the backend
 * @param {number}  widthMm / heightMm  media size
 * @returns {Promise<{svg: string, widthMm: number, heightMm: number,
 *          drawn: number}|null>}
 *          null when the layout has nothing printable (unrecognised language,
 *          or every field unmapped) so the caller can fall back.
 */
export async function labelToSVG({ parsed, resolved, widthMm, heightMm }) {
  const els = parsed?.elements || [];
  if (!els.length) return null;

  const drawQr = els.some((e) => e.kind === "qr")
    ? await ensureQrRenderer()
    : null;

  const wMm = widthMm || parsed?.declared?.wMm || DEFAULT_LABEL_MM.w;
  const hMm = heightMm || parsed?.declared?.hMm || DEFAULT_LABEL_MM.h;
  const W = Math.max(8, Math.round(wMm * DPMM));
  const H = Math.max(8, Math.round(hMm * DPMM));

  const parts = [];
  let drawn = 0;

  for (const el of els) {
    const { text, missing } = fillTokens(el.raw, resolved);

    // A field with nothing to print is not drawn — the printer leaves that
    // space blank too.
    if (el.kind !== "box" && !text) continue;

    const rot = el.rot ? ` transform="rotate(${el.rot} ${el.x} ${el.y})"` : "";

    if (el.kind === "box") {
      parts.push(
        `<rect ${attr({
          x: el.x,
          y: el.y,
          width: Math.max(0, el.w),
          height: Math.max(0, el.h),
          fill: el.filled ? "#000" : "none",
          stroke: el.filled ? undefined : "#000",
          "stroke-width": el.filled ? undefined : el.thickness || 1,
        })}${rot}/>`,
      );
      drawn += 1;
      continue;
    }

    if (el.kind === "qr") {
      // ^FT gives the baseline/bottom-left; the symbol size is only known once
      // the payload is, so the origin is corrected here.
      const size = qrModules(text) * Math.max(1, el.mag || 4);
      const y = el.baseline ? el.y - size : el.y;
      const markup = drawQr({
        value: text,
        size,
        level: "M",
        marginSize: 0,
        bgColor: "#FFFFFF",
        fgColor: "#000000",
      });
      parts.push(
        `<g transform="translate(${el.x} ${y})${
          el.rot ? ` rotate(${el.rot})` : ""
        }">${markup}</g>`,
      );
      drawn += 1;
      continue;
    }

    if (el.kind === "barcode") {
      // A barcode with any token left unresolved is dropped rather than
      // encoded from what did resolve: a half-filled key scans cleanly and
      // sends the pouch to the wrong loan, which is worse than no bars at all.
      if (missing) continue;
      const bars =
        el.symbology === "code39"
          ? code39Bars(text, el.ratio)
          : code128Bars(text);
      if (!bars.width) continue;

      const lineH = Math.max(12, el.height * 0.22);
      const barsY = el.y + (el.above ? lineH : 0);
      const body = bars.bars
        .map((b) =>
          rect(
            el.x + b.x * el.module,
            barsY,
            b.w * el.module,
            el.height,
            "#000",
          ),
        )
        .join("");
      const human = el.human
        ? `<text ${attr({
            x: el.x + (bars.width * el.module) / 2,
            y: el.above ? el.y + lineH - 2 : el.y + el.height + lineH - 2,
            "font-size": lineH,
            fill: "#000",
            "text-anchor": "middle",
            "font-family": "Arial, Helvetica, sans-serif",
          })}>${esc(text)}</text>`
        : "";
      parts.push(`<g${rot}>${body}${human}</g>`);
      drawn += 1;
      continue;
    }

    // Text. ^FO places the top of the character cell, ^FT its baseline.
    const y = el.baseline ? el.y : el.y + el.h * 0.82;
    // Squeeze a fixed-pitch face onto the printer's declared cell so a
    // column-aligned design still lines up; proportional faces are left alone.
    const sx =
      el.mono && el.w > 0 && el.h > 0
        ? Math.max(0.4, Math.min(2, el.w / (el.h * 0.6)))
        : 1;
    const scale =
      sx !== 1
        ? ` translate(${el.x} 0) scale(${sx.toFixed(
            3,
          )} 1) translate(${-el.x} 0)`
        : "";
    const invert =
      el.invert && text
        ? rect(el.x - 2, el.y, charWidth(el) * text.length + 4, el.h, "#000")
        : "";

    const xform = `${
      el.rot ? `rotate(${el.rot} ${el.x} ${el.y})` : ""
    }${scale}`;

    parts.push(
      `<g${xform ? ` transform="${xform}"` : ""}>${invert}<text ${attr({
        x: el.x,
        y,
        "font-size": el.h,
        fill: el.invert ? "#FFF" : "#000",
        "font-family": el.mono
          ? "Consolas, 'Courier New', monospace"
          : "Arial, Helvetica, sans-serif",
        "xml:space": "preserve",
      })}>${esc(text)}</text></g>`,
    );
    drawn += 1;
  }

  if (!drawn) return null;

  return {
    svg: svgDocument(parts.join(""), W, H, wMm, hMm),
    widthMm: wMm,
    heightMm: hMm,
    drawn,
  };
}

// The media rectangle is painted white and everything is clipped to it, so a
// field the design pushes off the edge is cut exactly where the printer cuts
// it instead of stretching the page.
const svgDocument = (body, W, H, wMm, hMm) =>
  `<svg xmlns="http://www.w3.org/2000/svg" width="${wMm}mm" height="${hMm}mm" ` +
  `viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid meet" ` +
  `shape-rendering="crispEdges">` +
  `<clipPath id="labelclip"><rect x="0" y="0" width="${W}" height="${H}"/></clipPath>` +
  `<rect x="0" y="0" width="${W}" height="${H}" fill="#fff"/>` +
  `<g clip-path="url(#labelclip)">${body}</g>` +
  `</svg>`;

// ─── Built-in sticker ───────────────────────────────────────────────────────

// Rough advance width for Arial at a given cap height, in dots. Only used to
// decide when a value has to be trimmed, so an estimate is enough — but it
// errs wide (label text is mostly upper case, which runs above the mixed-case
// average) because a value that spills off the edge is worse than one trimmed
// a character early.
const ADVANCE = 0.62;
const textWidth = (s, size) => String(s).length * size * ADVANCE;

const fit = (s, maxDots, size) => {
  const str = String(s == null ? "" : s).trim();
  if (!str) return "";
  if (textWidth(str, size) <= maxDots) return str;
  const keep = Math.max(1, Math.floor(maxDots / (size * ADVANCE)) - 1);
  return `${str.slice(0, keep)}…`;
};

// Break a value across at most `maxLines` lines that each fit `maxDots`, at word
// boundaries where there is one. Only the last line is ever trimmed, and only
// when the text still does not fit.
//
// Why names get a second line: a truncated name is not a shorter name, it is a
// different person — "Mahadev Prasad Pal" and "Mahadev Prasad Rao" both print as
// "Mahadev Prasad …", so the one value an operator uses to match a pouch to its
// customer stops distinguishing them exactly when it is longest. Two lines cost
// vertical space the label has; the cut costs identification, which it does not.
//
// Leading between the wrapped lines is WRAP_LEAD of the cap height — tighter than
// the gap between fields, so a wrapped name still reads as one value.
const WRAP_LEAD = 0.15;
const wrapLead = (size) => Math.max(1, Math.round(size * WRAP_LEAD));

const wrapFit = (s, maxDots, size, maxLines = 1) => {
  const str = String(s == null ? "" : s)
    .trim()
    .replace(/\s+/g, " ");
  if (!str) return [];

  // Character budget rather than a per-substring measure: the width model here
  // is a fixed advance anyway (see ADVANCE), so counting is the same estimate
  // and keeps this in step with fit()'s idea of what overflows.
  const perLine = Math.max(1, Math.floor(maxDots / (size * ADVANCE)));
  if (str.length <= perLine) return [str];

  const lines = [];
  let rest = str;
  while (rest && lines.length < maxLines - 1) {
    if (rest.length <= perLine) break;
    // Break at the last space that fits; a single word longer than the column
    // (one unspaced name, a scanned barcode string) is split mid-word instead,
    // which is still more of it than an ellipsis would show.
    let cut = rest.lastIndexOf(" ", perLine);
    if (cut <= 0) cut = perLine;
    lines.push(rest.slice(0, cut).trim());
    rest = rest.slice(cut).trim();
  }
  if (rest) lines.push(fit(rest, maxDots, size));
  return lines;
};

/**
 * The standard pouch sticker, drawn from scratch.
 *
 * Used when this store has no PRN template, or has one in a language whose
 * layout cannot be parsed (SBPL) — and, in practice, on every handheld whose
 * label stock is too far off the tag roll the store's template was drawn for.
 *
 * **Its layout deliberately copies the store's SATO tag** (2026-08-12, at the
 * user's request): two columns, store name above the QR on the left, then
 * voucher → customer → gross weight → quantity → purity stacked on the right.
 * The point is that an operator holding a pouch stickered by the counter and one
 * stickered by the printer in their bag reads the same label twice, rather than
 * having to learn where each transport put the weight. Date and floor are not
 * drawn: they are not on the SATO tag either, and five rows is what the right
 * column holds at a size a 203 dpi head prints cleanly.
 *
 * The scan key is a QR of the voucher number. QR is square, so it stands in its
 * own column rather than banded across the label the way the old Code 128 was:
 * a 1D barcode wants width and almost no height, a QR wants both, and on 25 mm
 * stock a full-width band left it only ~7 mm to be square in. In a column it
 * gets the whole height below the store name — roughly 0.5 mm per module on
 * 50 × 25 mm, comfortably above what a phone camera or a 2D imager resolves.
 *
 * Async because the QR encoder is lazy-loaded (see ensureQrRenderer).
 */
export async function builtInStickerSVG(
  { voucherNo, storeName, customerName, grossWeight, pieces, purity },
  { widthMm = DEFAULT_LABEL_MM.w, heightMm = DEFAULT_LABEL_MM.h } = {},
) {
  const W = Math.round(widthMm * DPMM);
  const H = Math.round(heightMm * DPMM);
  const pad = Math.round(1.2 * DPMM); // 1.2 mm quiet margin on every edge
  const inner = W - pad * 2;

  const parts = [];
  const line = (x, y, size, text, { anchor = "start", bold = false } = {}) =>
    `<text ${attr({
      x,
      y,
      "font-size": size,
      "font-family": "Arial, Helvetica, sans-serif",
      "font-weight": bold ? 700 : 400,
      "text-anchor": anchor === "start" ? undefined : anchor,
      fill: "#000",
    })}>${esc(text)}</text>`;

  // ── Left column: store name, QR beneath it ────────────────────────────────
  //
  // The column is sized first and the QR takes what is left of the height, so
  // the store name never pushes the symbol off the bottom edge.
  const colW = Math.round(inner * 0.42);
  let leftY = pad;

  if (storeName) {
    // The store name sits over a column well under half the label, so a branch
    // name rarely fits on one line at full size. It shrinks a little, then wraps
    // to a second line, and only then is it cut: the two lines cost the QR about
    // 1.5 mm of height, which it can afford — see the side cap below.
    // The floor is low enough to absorb a name that overflows by a few percent:
    // "Test Pawn Shop" on 75 × 50 mm stock misses the column by a fraction of a
    // dot, and one step smaller reads better than a two-line "Test Pawn / Shop".
    // Below ~1.6 mm, wrapping wins instead — a pouch label is read at arm's
    // length, off a curved surface.
    let size = Math.round(H * 0.09);
    const floorSize = Math.max(8, Math.round(H * 0.065));
    while (size > floorSize && textWidth(storeName, size) > colW) size -= 1;

    const nameLines = wrapFit(storeName, colW, size, 2);
    nameLines.forEach((text, i) => {
      parts.push(line(pad, leftY + size, size, text, { bold: true }));
      leftY += size + (i < nameLines.length - 1 ? wrapLead(size) : 0);
    });
    leftY += Math.round(0.5 * DPMM);
  }

  // Only the voucher is encoded — it never changes, so a later scan resolves
  // the loan's *current* details from the DB rather than a stale copy printed on
  // the pouch. The symbol carries its full 4-module quiet zone inside `size`
  // (marginSize: 4) instead of relying on the surrounding layout: a QR crowded
  // on one side is the classic "reads on the bench, fails on the counter" label.
  const qrText = String(voucherNo == null ? "" : voucherNo).trim();
  const qrRoom = H - pad - leftY;

  if (qrText && qrRoom > 8) {
    const drawQr = await ensureQrRenderer();
    const side = Math.max(8, Math.round(Math.min(qrRoom, colW)));
    parts.push(
      `<g transform="translate(${pad} ${leftY})">${drawQr({
        value: qrText,
        size: side,
        level: "M",
        marginSize: 4,
        bgColor: "#FFFFFF",
        fgColor: "#000000",
      })}</g>`,
    );
  }

  // ── Right column: voucher, customer, weight, quantity, purity ─────────────
  //
  // The label carries its own separator, so label and value are joined verbatim
  // — it is this layout that decides whether a field reads "G: 7.750g" or
  // "Gr 7.750g". A missing value drops its label with it; a zero is a value.
  const pair = (label, value) =>
    value == null || value === "" ? "" : `${label}${value}`;

  const detailX = pad + colW + Math.round(0.8 * DPMM);
  const detailW = Math.max(0, W - pad - detailX);

  // Sizes are relative so the block scales with the stock, and the voucher is
  // set larger than the rest: it is the human fallback for a scuffed QR and the
  // value anyone reads first. Only the customer name may wrap — the others are
  // short by construction, and a wrapped "G: 18.450g" would read as two values.
  const rows = [
    { text: qrText, scale: 1.2, bold: true },
    { text: String(customerName || "").trim(), scale: 1, bold: true, lines: 2 },
    {
      text: pair(
        "G: ",
        grossWeight != null && grossWeight !== "" ? `${grossWeight}g` : "",
      ),
      scale: 0.95,
      bold: true,
    },
    { text: pair("Q: ", pieces), scale: 0.95 },
    { text: pair("P: ", purity), scale: 0.95 },
  ].filter((r) => r.text);

  // Fit the whole stack by shrinking rather than by dropping rows: on tall stock
  // every row is comfortable anyway, and on short stock a quantity that silently
  // vanished would be read as "no quantity recorded". The wrap is measured inside
  // the loop because how many lines the name needs depends on the size being
  // tried — shrinking a step can be what takes it back to one line.
  const lead = Math.max(2, Math.round(H * 0.018));
  const layoutAt = (base) =>
    rows.map((r) => {
      const size = Math.round(base * r.scale);
      return { ...r, size, wrapped: wrapFit(r.text, detailW, size, r.lines) };
    });
  const stackHeight = (laid) =>
    laid.reduce(
      (h, r) =>
        h +
        r.wrapped.length * r.size +
        (r.wrapped.length - 1) * wrapLead(r.size) +
        lead,
      -lead,
    );

  let base = Math.round(H * 0.105);
  let laid = layoutAt(base);
  while (base > 7 && stackHeight(laid) > H - pad * 2) laid = layoutAt(--base);

  let y = pad;
  for (const row of laid) {
    for (let i = 0; i < row.wrapped.length; i += 1) {
      parts.push(
        line(detailX, y + row.size, row.size, row.wrapped[i], {
          bold: row.bold,
        }),
      );
      y += row.size + (i < row.wrapped.length - 1 ? wrapLead(row.size) : 0);
    }
    y += lead;
  }

  return {
    svg: svgDocument(parts.join(""), W, H, widthMm, heightMm),
    widthMm,
    heightMm,
    drawn: parts.length,
  };
}

// ─── Media ──────────────────────────────────────────────────────────────────
//
// Which label stock is in the printer is a property of the *device*, not of the
// store: the counter's SATO holds the tag roll the template was designed for,
// while the phone in someone's pocket is paired with a 50 × 25 mm pouch-label
// printer. Nothing on the server knows which, and getting it wrong is not a
// cosmetic error — a page wider than the media is centre-cropped by the print
// service, which is how a label comes out holding only its middle third.
//
// So the operator states it, once, per device.
export const MEDIA_PRESETS = [
  { w: 50, h: 25 },
  { w: 50, h: 30 },
  { w: 38, h: 25 },
  { w: 58, h: 40 },
  { w: 75, h: 50 },
  { w: 100, h: 50 },
];

export const mediaKey = (m) => `${m.w}x${m.h}`;
export const mediaLabel = (m) => `${m.w} × ${m.h} mm`;

// Which stock is loaded, and whether the operator overrode the design choice —
// remembered per browser rather than per user, for the same reason: it
// describes the hardware in front of them. Both the print-dialog path and the
// Bluetooth path read these, so the two never disagree about the media.
export const LABEL_MEDIA_KEY = "pawn.labelMedia";
export const LABEL_DESIGN_KEY = "pawn.labelDesign";

export const readLabelPref = (key, fallback) => {
  try {
    return window.localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
};

export const writeLabelPref = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* private browsing — the choice just does not stick */
  }
};

/** Parse a "50x25" key back to a size, falling back to the 50 × 25 default. */
export const mediaFromKey = (key) => {
  const m = /^([\d.]+)x([\d.]+)$/.exec(String(key || ""));
  if (!m) return { ...DEFAULT_LABEL_MM };
  return { w: parseFloat(m[1]), h: parseFloat(m[2]) };
};

/**
 * Media list for the picker: the presets, plus whatever size the store's
 * template declares — a branch that really did load the tag stock the template
 * was drawn for needs to be able to say so.
 *
 * A template that declares a width but no length (normal for continuous stock)
 * still gets an entry: its width paired with the height of the stock currently
 * selected. Dropping it because the length was absent would leave the operator
 * with no way to pick the one size their own design fits.
 */
export const mediaOptionsFor = (declared, media) => {
  const out = MEDIA_PRESETS.map((m) => ({ ...m }));
  const w = declared?.wMm;
  const h = declared?.hMm || media?.h || DEFAULT_LABEL_MM.h;
  if (
    w &&
    h &&
    !out.some((m) => Math.abs(m.w - w) < 0.5 && Math.abs(m.h - h) < 0.5)
  )
    out.push({ w, h, template: true });
  return out;
};

// Whether the store's PRN design can be printed on this media at its true size.
// A design drawn for 89 mm tag stock, squeezed onto a 50 mm pouch label, loses
// 44% of every dimension: 2 mm text becomes 1.1 mm and the barcode's narrow bar
// drops under the width a handheld scanner can resolve. Below the tolerance it
// is the wrong design for the media, not a smaller version of the right one.
export const templateFitsMedia = (declared, media, tolMm = 3) =>
  !!declared?.wMm &&
  Math.abs(declared.wMm - media.w) <= tolMm &&
  (!declared.hMm || Math.abs(declared.hMm - media.h) <= tolMm);

// ─── Print document ─────────────────────────────────────────────────────────

/**
 * Wrap a label SVG in a page that prints as exactly one label, with the media
 * and design controls the operator needs to correct it in place.
 *
 * The rules that keep it to one page, in order of how often each one is what
 * went wrong:
 *   • the page is the size of the media the operator says is loaded — not the
 *     size of the design. A page wider than the media gets centre-cropped;
 *     a page taller than the label paginates.
 *   • the only thing in <body> is the SVG at width:100% — nothing can reflow,
 *     so nothing can spill onto a second page.
 *   • break-inside/page-break-inside: avoid, for the print services that lay
 *     the page out before honouring @page.
 *
 * The selects call back into the opener (window.onLabelOptions) rather than
 * re-rendering here: the label renderer lives in one place and this page stays
 * a view of it.
 */
export function labelPrintHTML({
  svg,
  media,
  title,
  designMode = "auto",
  designNote = "",
  mediaOptions = MEDIA_PRESETS,
  autoPrint = true,
  handheld = false,
}) {
  const m = media || { ...DEFAULT_LABEL_MM };
  const selected = mediaKey(m);

  // Orientation. Chrome derives the print dialog's default from the @page box:
  // a label is wider than it is tall, so an explicit `50mm 25mm` reads as
  // landscape and the operator has to correct it on every single print.
  //
  // On a phone that is the wrong trade anyway — Chrome for Android ignores the
  // @page *size* and lays out against whatever paper the print service offers,
  // so the millimetres buy nothing there. Asking only for `portrait` keeps the
  // one part Android does honour and makes the dialog open correctly by
  // default. Desktop, where the page box is real and respected, keeps the exact
  // media size.
  //
  // Portrait is right for a roll: the media is 50 mm across and feeds
  // lengthwise, so the *page* is tall even though the label on it is wide.
  const pageRule = handheld
    ? "size: portrait;"
    : `size: ${m.w}mm ${m.h}mm;`;

  const mediaOpts = mediaOptions
    .map(
      (o) =>
        `<option value="${esc(mediaKey(o))}"${
          mediaKey(o) === selected ? " selected" : ""
        }>${esc(mediaLabel(o))}${
          o.template ? " — template size" : ""
        }</option>`,
    )
    .join("");

  const designOpts = [
    ["auto", "Automatic"],
    ["template", "Store template"],
    ["sticker", "Standard sticker"],
  ]
    .map(
      ([v, l]) =>
        `<option value="${v}"${
          v === designMode ? " selected" : ""
        }>${l}</option>`,
    )
    .join("");

  return `<!doctype html><html><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(title || "Label")}</title>
<style>
  @page { ${pageRule} margin: 0; }
  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; background: #fff; }
  .sheet { break-inside: avoid; page-break-inside: avoid; }
  .sheet svg { display: block; width: 100%; height: auto; }
  .panel { font: 14px/1.5 Arial, Helvetica, sans-serif; color: #CBD5E1;
           max-width: 420px; margin: 18px auto 28px; padding: 0 14px; }
  .row { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 10px; }
  .row label { flex: 0 0 auto; }
  select { flex: 1 1 auto; min-width: 0; font: 15px Arial, Helvetica, sans-serif; padding: 9px 10px;
           border-radius: 8px; border: 1px solid #334155; background: #1E293B; color: #F8FAFC; }
  .note { font-size: 13px; color: #FCD34D; margin: 10px 0 0; }
  .tip { font-size: 12.5px; color: #94A3B8; margin: 10px 0 0; }
  .tip b { color: #E2E8F0; }
  button { width: 100%; font: 700 16px Arial, Helvetica, sans-serif; padding: 13px 22px; margin-top: 16px;
           border-radius: 10px; border: 0; background: #2563EB; color: #fff; }
  @media screen {
    body { background: #0F172A; min-height: 100vh; padding: 16px 12px; }
    /* Shown at twice life size so the label is legible on a phone before it is
       committed to media. Print resets this to the media width. */
    .sheet { width: min(92vw, ${m.w * 2}mm); margin: 0 auto;
             box-shadow: 0 6px 24px rgba(0,0,0,.45); background: #fff; }
  }
  @media print {
    /* Only the label may exist on the printed page. Widths stay at 100%
       rather than pinned to millimetres: Chrome on Android ignores @page size
       and lays out against whatever paper the print service offers, so the
       label has to take the page's width, not assume it. */
    .panel { display: none !important; }
    html, body { width: 100%; overflow: hidden; background: #fff; padding: 0; }
    .sheet { width: 100%; margin: 0; }
    * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  }
</style></head><body>
<div class="sheet">${svg}</div>
<div class="panel">
  <div class="row"><label for="media">Label stock</label><select id="media">${mediaOpts}</select></div>
  <div class="row"><label for="design">Design</label><select id="design">${designOpts}</select></div>
  ${designNote ? `<p class="note">${esc(designNote)}</p>` : ""}
  <p class="tip">In the print dialog choose paper <b>${m.w} mm</b> — it must match
  the stock above, because a paper wider than the label is cropped, not shrunk.
  Margins <b>none</b>, scale <b>100%</b>${
    handheld ? "" : ", orientation <b>portrait</b>"
  }.</p>
  <button id="go">Print</button>
</div>
<script>
(function () {
  var media = document.getElementById('media');
  var design = document.getElementById('design');
  var apply = function () {
    if (window.onLabelOptions) window.onLabelOptions(media.value, design.value);
  };
  media.onchange = apply;
  design.onchange = apply;
  document.getElementById('go').onclick = function () { window.print(); };
  ${autoPrint ? "setTimeout(function () { window.print(); }, 350);" : ""}
})();
</${""}script>
</body></html>`;
}
